import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { KeyRound, Mail, UserPlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { CustomToaster } from "@/components/general/CustomToaster";
import { IconButton } from "@/components/v2/buttons/IconButton";
import { Input } from "@/components/v2/inputs/Input";
import { authClient, signIn, useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { getBackendErr } from "@/utils/genUtils";
import { isPasskeyConditionalUiAvailable } from "./authPasskey";
import { redirectAfterAuthSessionReady } from "./authRedirect";
import { AuthBackground } from "./components/AuthBackground";
import { AutumnWordmark } from "./components/AutumnWordmark";
import { OTPSignIn } from "./components/OTPSignIn";

/**
 * Check if URL has OAuth parameters (from OAuth provider redirect)
 * These params are added by better-auth when redirecting unauthenticated users
 */
function getOAuthRedirectUrl(searchParams: URLSearchParams): string | null {
	const clientId = searchParams.get("client_id");
	const responseType = searchParams.get("response_type");
	const redirectUri = searchParams.get("redirect_uri");
	if (clientId && responseType && redirectUri) {
		const backendUrl = import.meta.env.VITE_BACKEND_URL;
		return `${backendUrl}/api/auth/oauth2/authorize?${searchParams.toString()}`;
	}
	return null;
}

export const emailRegex = /^[^@]+@[^@]+\.[^@]+$/;
type AuthMode = "otp" | "password-sign-in" | "password-sign-up";
type OtpMode = "sign-in" | "email-verification";

export const SignIn = () => {
	const [email, setEmail] = useState("");
	const [name, setName] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [googleLoading, setGoogleLoading] = useState(false);
	const [sendOtpLoading, setSendOtpLoading] = useState(false);
	const [passwordLoading, setPasswordLoading] = useState(false);
	const [otpSent, setOtpSent] = useState(false);
	const [authMode, setAuthMode] = useState<AuthMode>("otp");
	const [otpMode, setOtpMode] = useState<OtpMode>("sign-in");
	const [authRedirecting, setAuthRedirecting] = useState(false);

	const { data: session } = useSession();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const oauthRedirectUrl = useMemo(
		() => getOAuthRedirectUrl(searchParams),
		[searchParams],
	);

	const defaultNewPath = "/sandbox/products?tab=products";
	const defaultCallbackPath = "/sandbox/products?tab=products";

	const newPath = oauthRedirectUrl || defaultNewPath;
	const callbackPath = oauthRedirectUrl || defaultCallbackPath;
	const redirectAfterAuth = async (targetUrl: string) => {
		setAuthRedirecting(true);
		const redirected = await redirectAfterAuthSessionReady({
			targetUrl,
			getSession: () => authClient.getSession(),
			redirect: (url) => {
				window.location.href = url;
			},
			onMissingSession: (message) => {
				toast.error(message);
			},
			onError: (error) => {
				toast.error(getBackendErr(error, "Could not load your session."));
			},
		});
		if (!redirected) setAuthRedirecting(false);
		return redirected;
	};

	useEffect(() => {
		if (oauthRedirectUrl || authRedirecting) return;
		if (session) {
			navigate("/", { replace: true });
		}
	}, [session, navigate, oauthRedirectUrl, authRedirecting]);

	// Passkey Conditional UI: browsers surface saved passkeys directly in the
	// email field's autocomplete dropdown (no extra button needed). Requires
	// the `webauthn` token in autoComplete and `autoFill: true` on signIn.
	// Skipped during OAuth flows since the post-auth redirect would be lost.
	useEffect(() => {
		if (oauthRedirectUrl || session) return;
		if (typeof window === "undefined") return;
		const controller = new AbortController();
		(async () => {
			try {
				if (!(await isPasskeyConditionalUiAvailable(window))) return;
				await authClient.signIn.passkey({
					autoFill: true,
					fetchOptions: { signal: controller.signal },
				});
			} catch {
				// Aborts, cancels, and unsupported-browser errors are non-fatal.
			}
		})();
		return () => {
			controller.abort();
		};
	}, [oauthRedirectUrl, session]);

	const handleEmailSignIn = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!email || !emailRegex.test(email)) {
			toast.error("Please enter a valid email address.");
			return;
		}
		setSendOtpLoading(true);
		try {
			const { error } = await authClient.emailOtp.sendVerificationOtp({
				email: email,
				type: "sign-in",
			});
			if (error) {
				toast.error(
					error.message || "Something went wrong. Please try again.",
				);
			} else {
				setOtpMode("sign-in");
				setOtpSent(true);
			}
		} catch {
			toast.error("Something went wrong. Please try again.");
		} finally {
			setSendOtpLoading(false);
		}
	};

	const handlePasswordSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!email || !emailRegex.test(email)) {
			toast.error("Please enter a valid email address.");
			return;
		}
		if (password.length < 8) {
			toast.error("Password must be at least 8 characters.");
			return;
		}
		if (authMode === "password-sign-up") {
			if (!name.trim()) {
				toast.error("Please enter your name.");
				return;
			}
			if (password !== confirmPassword) {
				toast.error("Passwords do not match.");
				return;
			}
		}

		setPasswordLoading(true);
		try {
			if (authMode === "password-sign-up") {
				const { error } = await authClient.signUp.email({
					email,
					password,
					name: name.trim(),
					callbackURL: newPath,
				});
				if (error) {
					toast.error(error.message || "Could not create your account.");
					return;
				}
				setOtpMode("email-verification");
				setOtpSent(true);
				return;
			}

			const { error } = await authClient.signIn.email({
				email,
				password,
			});
			if (error) {
				const message = error.message || "";
				const code = (error as { code?: string }).code;
				if (code === "EMAIL_NOT_VERIFIED" || /email.*not.*verified/i.test(message)) {
					setOtpMode("email-verification");
					setOtpSent(true);
					toast.message("Check your email for a verification code.");
					return;
				}
				toast.error(message || "Could not sign in.");
				return;
			}
			await redirectAfterAuth(callbackPath);
		} catch (error) {
			toast.error(
				getBackendErr(error, "Something went wrong. Please try again."),
			);
		} finally {
			setPasswordLoading(false);
		}
	};

	const handleGoogleSignIn = async () => {
		setGoogleLoading(true);
		try {
			const frontendUrl = import.meta.env.VITE_FRONTEND_URL;
			const googleCallbackUrl =
				oauthRedirectUrl || `${frontendUrl}${defaultCallbackPath}`;
			const googleNewUserUrl =
				oauthRedirectUrl || `${frontendUrl}${defaultNewPath}`;
			const { error } = await signIn.social({
				provider: "google",
				callbackURL: googleCallbackUrl,
				newUserCallbackURL: googleNewUserUrl,
			});
			if (error) {
				toast.error(error.message || "Failed to sign in with Google");
			}
		} catch (error) {
			toast.error(getBackendErr(error, "Failed to sign in with Google"));
		} finally {
			setTimeout(() => setGoogleLoading(false), 1000);
		}
	};

	return (
		<AuthBackground>
			<CustomToaster />
			<div className="flex flex-col items-center gap-6">
				{/* Wordmark logo + welcome text */}
				<div className="flex flex-col items-center gap-3">
					<AutumnWordmark className="h-7 w-auto text-foreground" />
					<p className="text-sm text-muted-foreground">
						Welcome to Autumn, sign in to continue
					</p>
				</div>

				{otpSent ? (
					<OTPSignIn
						email={email}
						newPath={newPath}
						callbackPath={callbackPath}
						mode={otpMode}
						onAuthReady={redirectAfterAuth}
					/>
				) : (
					<div className="w-full space-y-5">
						<IconButton
							variant="primary"
							onClick={handleGoogleSignIn}
							isLoading={googleLoading}
							icon={<FontAwesomeIcon icon={faGoogle} />}
							className="w-full gap-2"
						>
							Continue with Google
						</IconButton>

						<div className="relative">
							<div className="absolute inset-0 flex items-center">
								<span className="w-full border-t border-border" />
							</div>
							<div className="relative flex justify-center text-xs uppercase">
								<span className="bg-background px-2 text-muted-foreground">
									Or
								</span>
							</div>
						</div>

						<div className="grid grid-cols-3 gap-1 rounded-lg border border-border bg-muted p-1">
							<button
								type="button"
								onClick={() => setAuthMode("otp")}
								className={cn(
									"h-8 rounded-md text-xs font-medium",
									authMode === "otp"
										? "bg-background text-foreground shadow-sm"
										: "text-muted-foreground",
								)}
							>
								Code
							</button>
							<button
								type="button"
								onClick={() => setAuthMode("password-sign-in")}
								className={cn(
									"h-8 rounded-md text-xs font-medium",
									authMode === "password-sign-in"
										? "bg-background text-foreground shadow-sm"
										: "text-muted-foreground",
								)}
							>
								Password
							</button>
							<button
								type="button"
								onClick={() => setAuthMode("password-sign-up")}
								className={cn(
									"h-8 rounded-md text-xs font-medium",
									authMode === "password-sign-up"
										? "bg-background text-foreground shadow-sm"
										: "text-muted-foreground",
								)}
							>
								Sign up
							</button>
						</div>

						{authMode === "otp" ? (
							<form
								className="flex flex-col gap-2 w-full"
								onSubmit={handleEmailSignIn}
							>
								<Input
									type="email"
									placeholder="Email or passkey"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									required
									className="text-base !w-full"
									// "webauthn" token activates Passkey Conditional UI on
									// Chromium/Safari — saved passkeys appear in the input's
									// autofill dropdown.
									autoComplete="username webauthn"
								/>
								<IconButton
									type="submit"
									variant="secondary"
									isLoading={sendOtpLoading}
									className="gap-2 w-full"
									icon={<Mail size={14} className="text-subtle" />}
								>
									Continue with Email
								</IconButton>
							</form>
						) : (
							<form
								className="flex flex-col gap-2 w-full"
								onSubmit={handlePasswordSubmit}
							>
								{authMode === "password-sign-up" && (
									<Input
										type="text"
										placeholder="Name"
										value={name}
										onChange={(e) => setName(e.target.value)}
										required
										className="text-base !w-full"
										autoComplete="name"
									/>
								)}
								<Input
									type="email"
									placeholder="Email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									required
									className="text-base !w-full"
									autoComplete="username"
								/>
								<Input
									type="password"
									placeholder="Password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									required
									className="text-base !w-full"
									autoComplete={
										authMode === "password-sign-up"
											? "new-password"
											: "current-password"
									}
								/>
								{authMode === "password-sign-up" && (
									<Input
										type="password"
										placeholder="Confirm password"
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
										required
										className="text-base !w-full"
										autoComplete="new-password"
									/>
								)}
								<IconButton
									type="submit"
									variant="secondary"
									isLoading={passwordLoading}
									className="gap-2 w-full"
									icon={
										authMode === "password-sign-up" ? (
											<UserPlus size={14} className="text-subtle" />
										) : (
											<KeyRound size={14} className="text-subtle" />
										)
									}
								>
									{authMode === "password-sign-up"
										? "Create Account"
										: "Sign in with Password"}
								</IconButton>
							</form>
						)}
					</div>
				)}
			</div>
		</AuthBackground>
	);
};
